import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  Typography,
  Paper,
  Divider,
  Autocomplete,
} from "@mui/material";
import { Add, Delete, PlaylistAdd, Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import {
  fetchAllWarehouses,
  fetchAllItems,
  addInitialStock,
  fetchWarehouseItems,
  fetchAllItemCategories,
} from "../../api/InventoryService";

const InitialStockPage = () => {
  const [warehouses, setWarehouses] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [existingItemIds, setExistingItemIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Each row: { category_id, item_id, quantity, low_stock_threshold }
  const [rows, setRows] = useState([
    { category_id: "", item_id: "", quantity: "", low_stock_threshold: "" },
  ]);

  useEffect(() => {
    const load = async () => {
      try {
        const [wRes, iRes, cRes] = await Promise.all([
          fetchAllWarehouses(),
          fetchAllItems(),
          fetchAllItemCategories(),
        ]);
        setWarehouses(wRes.data || []);
        setAllItems(iRes.data || []);
        setCategories(cRes.data?.data || cRes.data || []);
      } catch (e) {
        toast.error("Failed to load data");
      }
    };
    load();
  }, []);

  const handleWarehouseChange = async (id) => {
    setWarehouseId(id);
    setRows([{ category_id: "", item_id: "", quantity: "", low_stock_threshold: "" }]);
    if (id) {
      try {
        const res = await fetchWarehouseItems(id);
        const ids = (res.data || []).map((wi) => wi.item_id);
        setExistingItemIds(ids);
      } catch (e) {
        setExistingItemIds([]);
      }
    } else {
      setExistingItemIds([]);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      { category_id: "", item_id: "", quantity: "", low_stock_threshold: "" },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    // When category changes, clear the item selection
    if (field === "category_id") {
      updated[index].item_id = "";
    }
    setRows(updated);
  };

  // Items already selected in other rows (to prevent duplicates)
  const selectedItemIds = rows.map((r) => r.item_id).filter(Boolean);

  const handleSubmit = async () => {
    if (!warehouseId) {
      return toast.warning("Please select a warehouse");
    }

    const validRows = rows.filter((r) => r.item_id && r.quantity > 0);
    if (validRows.length === 0) {
      return toast.warning(
        "Please add at least one item with a valid quantity",
      );
    }

    // Check for duplicate items
    const itemIds = validRows.map((r) => r.item_id);
    if (new Set(itemIds).size !== itemIds.length) {
      return toast.warning("Duplicate items found. Each item can only appear once.");
    }

    setSaving(true);
    try {
      const payload = {
        warehouse_id: warehouseId,
        items: validRows.map((r) => ({
          item_id: r.item_id,
          quantity: parseInt(r.quantity),
          low_stock_threshold: r.low_stock_threshold
            ? parseInt(r.low_stock_threshold)
            : 0,
        })),
      };

      const res = await addInitialStock(payload);
      toast.success(res.message || "Initial stock added successfully!");

      // Reset form and reload existing items for the warehouse
      setRows([{ category_id: "", item_id: "", quantity: "", low_stock_threshold: "" }]);
      if (warehouseId) {
        try {
          const wRes = await fetchWarehouseItems(warehouseId);
          setExistingItemIds((wRes.data || []).map((wi) => wi.item_id));
        } catch (e) { /* ignore */ }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to add initial stock",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Add Initial / Existing Stock
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Use this page to add existing inventory items with their current
          quantities into a warehouse. Select the warehouse, then add items with
          their quantities below.
        </Typography>

        {/* Warehouse Selection */}
        <FormControl fullWidth required sx={{ mb: 3, maxWidth: 400 }}>
          <InputLabel>Select Warehouse</InputLabel>
          <Select
            label="Select Warehouse"
            value={warehouseId}
            onChange={(e) => handleWarehouseChange(e.target.value)}
          >
            {warehouses.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {w.name} — {w.location}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ mb: 3 }} />

        {/* Items List */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Items
        </Typography>

        {rows.map((row, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 2,
              mb: 2,
              alignItems: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            <Typography
              variant="body2"
              sx={{ minWidth: 30, fontWeight: "bold" }}
            >
              {index + 1}.
            </Typography>

            <FormControl size="small" sx={{ flex: 1, minWidth: 180 }} required>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={row.category_id}
                onChange={(e) =>
                  updateRow(index, "category_id", e.target.value)
                }
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 2, minWidth: 250 }}
              disabled={!row.category_id}
              options={allItems.filter(
                (i) =>
                  i.category_id === row.category_id &&
                  !existingItemIds.includes(i.id) &&
                  (!selectedItemIds.includes(i.id) || i.id === row.item_id),
              )}
              getOptionLabel={(option) =>
                option ? `${option.sku} — ${option.name}` : ""
              }
              value={allItems.find((i) => i.id === row.item_id) || null}
              onChange={(_, newValue) =>
                updateRow(index, "item_id", newValue ? newValue.id : "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    row.category_id ? "Item" : "Select category first"
                  }
                  required
                  size="small"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <TextField
              label="Quantity"
              type="number"
              required
              size="small"
              sx={{ flex: 1, minWidth: 120 }}
              inputProps={{ min: 1 }}
              value={row.quantity}
              onChange={(e) =>
                updateRow(
                  index,
                  "quantity",
                  e.target.value ? parseInt(e.target.value) : "",
                )
              }
            />

            <TextField
              label="Low Stock Alert"
              type="number"
              size="small"
              sx={{ flex: 1, minWidth: 140 }}
              inputProps={{ min: 0 }}
              value={row.low_stock_threshold}
              onChange={(e) =>
                updateRow(
                  index,
                  "low_stock_threshold",
                  e.target.value ? parseInt(e.target.value) : "",
                )
              }
              placeholder="0"
            />

            <IconButton
              color="error"
              onClick={() => removeRow(index)}
              disabled={rows.length === 1}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}

        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={addRow}
            sx={{ borderRadius: theme.button.borderRadius }}
          >
            Add Item Row
          </Button>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<Save />}
            onClick={handleSubmit}
            disabled={saving}
            sx={{ borderRadius: theme.button.borderRadius, px: 4 }}
          >
            {saving ? "Saving..." : `Save Initial Stock (${rows.filter((r) => r.item_id && r.quantity > 0).length} items)`}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default InitialStockPage;
